import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppData } from '@/app-data';
import { getReports, ReportBundle } from '@/features/reports/api';
import { searchPath } from '@/constants/routes';
import { useDocumentTitle } from '@/utils/use-document-title';
import MemberAvatar from '@/components/member-avatar';
import { ChevronDown } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';
import { AreaChart, BarChart, KpiTile, TypeDonut, WorkloadBars, ACCENT, VIOLET } from './charts';

const RANGES = [
  { key: '12w', label: '12 weeks', days: 84, granularity: 'week' as const },
  { key: '26w', label: '26 weeks', days: 182, granularity: 'week' as const },
  { key: '6m', label: '6 months', days: 182, granularity: 'month' as const },
  { key: '12m', label: '12 months', days: 365, granularity: 'month' as const },
];

const fmtBucket = (iso: string, gran: 'week' | 'month') => {
  const d = new Date(iso);
  return gran === 'month'
    ? d.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};
const fmtDays = (d: number) => (d >= 10 ? Math.round(d).toString() : d.toFixed(1).replace(/\.0$/, ''));

/** Cross-board analytics: completion throughput, cycle time, and current assignee workloads. */
export default function ReportsPage() {
  useDocumentTitle('Reports');
  const { boards, members } = useAppData();
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const [rangeKey, setRangeKey] = useState('12w');
  const [board, setBoard] = useState(''); // '' = all accessible boards
  const [data, setData] = useState<ReportBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const range = RANGES.find((r) => r.key === rangeKey) ?? RANGES[0];

  useEffect(() => {
    const s = ++seq.current;
    setLoading(true);
    getReports({ days: range.days, granularity: range.granularity, board: board || undefined })
      .then((r) => { if (s === seq.current) setData(r); })
      .catch(() => { if (s === seq.current) setData(null); })
      .finally(() => { if (s === seq.current) setLoading(false); });
  }, [range.days, range.granularity, board]);

  const periods = data?.periods ?? [];
  const gran = data?.granularity ?? range.granularity;
  const throughput = periods.map((p) => ({ label: fmtBucket(p.bucket, gran), value: p.completed, title: `${fmtBucket(p.bucket, gran)} · ${p.completed} completed` }));
  const cycle = periods.map((p) => ({
    label: fmtBucket(p.bucket, gran),
    value: p.avg_cycle_days,
    title: p.cycle_count > 0 ? `${fmtBucket(p.bucket, gran)} · ${fmtDays(p.avg_cycle_days)}d avg · ${p.cycle_count} completed` : `${fmtBucket(p.bucket, gran)} · no completions`,
  }));
  const perPeriodAvg = data && periods.length ? Math.round(data.total_completed / periods.length) : 0;

  // Click-through: open a NEW TAB with the global search that reproduces the clicked slice.
  const qval = (v: string) => (/[\s,]/.test(v) ? `"${v}"` : v);
  const boardClause = () => {
    if (!board) return '';
    const b = boards.find((x) => x.id === board);
    return b ? `board:${qval(b.name)}` : '';
  };
  const openSearch = (parts: (string | false)[]) => {
    const q = parts.filter(Boolean).join(' ').trim();
    if (q) window.open(searchPath(q), '_blank', 'noopener,noreferrer');
  };
  // Completion click-through: the `completed:FROM..TO` facet reproduces a throughput/cycle/type slice
  // exactly (same activity-trail definition Reports uses). Buckets are contiguous, so a bucket's window is
  // [its start, the next bucket's start) — the last bucket runs to range_end (now).
  const completedClause = (fromIso: string, toIso: string) => `completed:${fromIso}..${toIso}`;
  const pickPeriod = (i: number) => {
    if (!data) return;
    const p = periods[i];
    if (!p) return;
    openSearch([completedClause(p.bucket, periods[i + 1]?.bucket ?? data.range_end), boardClause()]);
  };
  const pickRange = (extra?: string) => {
    if (!data) return;
    openSearch([extra || false, completedClause(data.range_start, data.range_end), boardClause()]);
  };
  const pickWorkload = (sel: { assignee_id: string | null; name: string; type?: 'story' | 'task' | 'bug' }) =>
    openSearch([
      sel.assignee_id ? `assignee:${qval(sel.name)}` : 'assignee:none',
      sel.type ? `type:${sel.type}` : 'type:story,task,bug',
      'state:open',
      boardClause(),
    ]);

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      {/* Header + controls */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold text-fg-strong">Reports</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-faint">
            Delivery throughput, cycle time and open workload across {data && data.board_count > 0 ? `${data.board_count} board${data.board_count === 1 ? '' : 's'}` : 'your boards'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={board}
              onChange={(e) => setBoard(e.target.value)}
              className="h-9 appearance-none rounded-full border border-line bg-surface-inset pl-3.5 pr-9 text-[12.5px] font-medium text-fg-soft focus:border-accent/50 focus:outline-none"
            >
              <option value="">All boards</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-faint" />
          </div>
          <div className="flex items-center gap-0.5 rounded-full bg-layer p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRangeKey(r.key)}
                className={cn(
                  'rounded-full px-2.5 py-1.5 text-[12px] font-semibold transition-colors',
                  rangeKey === r.key ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-faint hover:text-fg-soft',
                )}
              >
                {r.key}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!data && loading ? (
        <div className="rounded-2xl border border-dashed border-line px-6 py-20 text-center text-[13px] text-fg-faint">Loading reports…</div>
      ) : !data ? (
        <div className="rounded-2xl border border-dashed border-line px-6 py-20 text-center text-[13px] text-fg-faint">Couldn’t load reports. Try again.</div>
      ) : (
        <div className={cn('space-y-4 transition-opacity', loading && 'opacity-60')}>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiTile label="Completed" value={data.total_completed.toLocaleString()} sub={range.label} color={ACCENT} onClick={() => pickRange()} />
            <KpiTile label="Avg cycle time" value={`${fmtDays(data.avg_cycle_days)}d`} sub={`median ${fmtDays(data.median_cycle_days)}d`} color={VIOLET} onClick={() => pickRange()} />
            <KpiTile label="Open items" value={data.total_open.toLocaleString()} sub="story · task · bug" onClick={() => openSearch(['type:story,task,bug', 'state:open', boardClause()])} />
            <KpiTile label={`Per ${gran}`} value={perPeriodAvg.toLocaleString()} sub="completed avg" onClick={() => pickRange()} />
          </div>

          {/* Throughput */}
          <section className="rounded-2xl border border-line bg-surface-raised p-5">
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="text-[14px] font-semibold text-fg-strong">Completed per {gran}</h2>
              <span className="text-[11.5px] text-fg-faint">{data.total_completed.toLocaleString()} total</span>
            </div>
            {data.total_completed === 0 ? (
              <div className="py-10 text-center text-[12.5px] text-fg-faint">No items completed in this range.</div>
            ) : (
              <BarChart data={throughput} onPick={pickPeriod} />
            )}
          </section>

          {/* Cycle time + completed-by-type */}
          <div className="grid gap-4 lg:grid-cols-3">
            <section className="rounded-2xl border border-line bg-surface-raised p-5 lg:col-span-2">
              <div className="mb-4 flex items-baseline justify-between">
                <h2 className="text-[14px] font-semibold text-fg-strong">Average cycle time</h2>
                <span className="text-[11.5px] text-fg-faint">start → done, in days</span>
              </div>
              <AreaChart data={cycle} onPick={pickPeriod} />
            </section>
            <section className="rounded-2xl border border-line bg-surface-raised p-5">
              <h2 className="mb-4 text-[14px] font-semibold text-fg-strong">Completed by type</h2>
              <TypeDonut data={data.completed_by_type} onPick={(type) => pickRange(`type:${type}`)} />
            </section>
          </div>

          {/* Workload */}
          <section className="rounded-2xl border border-line bg-surface-raised p-5">
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="text-[14px] font-semibold text-fg-strong">Open workload by assignee</h2>
              <span className="text-[11.5px] text-fg-faint">{data.total_open.toLocaleString()} open · stories, tasks & bugs not completed or dropped</span>
            </div>
            {data.workload.length === 0 ? (
              <div className="py-10 text-center text-[12.5px] text-fg-faint">No open work — everything’s done. 🎉</div>
            ) : (
              <WorkloadBars
                rows={data.workload}
                renderLead={(row) => <MemberAvatar member={row.assignee_id ? memberById.get(row.assignee_id) : undefined} size={20} className="shrink-0" />}
                onPick={pickWorkload}
              />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
